import { z } from "zod";

// ── prodotti ────────────────────────────────────────────────────────
export const productInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional().nullable(),
  sku: z.string().trim().max(60).optional().nullable(),
  unit: z.string().trim().max(20).default("pz"),
  unitPriceCents: z.coerce.number().int().min(0).default(0),
  vatRate: z.coerce.number().min(0).max(100).default(22),
  isActive: z.boolean().default(true),
});
export type ProductInput = z.infer<typeof productInputSchema>;

// ── righe documento (preventivi e fatture) ──────────────────────────
export const docItemSchema = z.object({
  productId: z.string().uuid().optional().nullable(),
  description: z.string().trim().min(1).max(500),
  quantity: z.coerce.number().min(0).max(1_000_000).default(1),
  unitPriceCents: z.coerce.number().int().min(0).default(0),
  vatRate: z.coerce.number().min(0).max(100).default(22),
  discountPercent: z.coerce.number().min(0).max(100).default(0),
});
export type DocItemInput = z.infer<typeof docItemSchema>;

/** Calcolo totali condiviso API/frontend. Tutto in centesimi interi. */
export function computeDocTotals(items: DocItemInput[]) {
  let subtotal = 0;
  let vat = 0;
  const lines = items.map((item) => {
    const base = Math.round(
      item.quantity * item.unitPriceCents * (1 - item.discountPercent / 100),
    );
    const lineVat = Math.round((base * item.vatRate) / 100);
    subtotal += base;
    vat += lineVat;
    return { ...item, lineTotalCents: base };
  });
  return { lines, subtotalCents: subtotal, vatCents: vat, totalCents: subtotal + vat };
}

// ── preventivi ──────────────────────────────────────────────────────
export const QUOTE_STATUSES = ["draft", "sent", "accepted", "rejected", "expired"] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const quoteInputSchema = z.object({
  title: z.string().trim().max(200).default(""),
  dealId: z.string().uuid().optional().nullable(),
  companyId: z.string().uuid().optional().nullable(),
  contactId: z.string().uuid().optional().nullable(),
  customerName: z.string().trim().max(200).default(""),
  customerEmail: z.string().trim().email().optional().nullable().or(z.literal("").transform(() => null)),
  customerAddress: z.string().trim().max(500).optional().nullable(),
  customerVat: z.string().trim().max(30).optional().nullable(),
  validUntil: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
  notes: z.string().trim().max(5000).optional().nullable(),
  items: z.array(docItemSchema).min(1).max(100),
});
export type QuoteInput = z.infer<typeof quoteInputSchema>;

export const quoteDecisionSchema = z.object({
  decision: z.enum(["accept", "reject"]),
  note: z.string().trim().max(1000).optional().nullable(),
});

// ── fatture ─────────────────────────────────────────────────────────
export const INVOICE_STATUSES = ["draft", "issued", "partially_paid", "paid", "canceled"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const invoiceInputSchema = z.object({
  companyId: z.string().uuid().optional().nullable(),
  contactId: z.string().uuid().optional().nullable(),
  customerName: z.string().trim().max(200).default(""),
  customerEmail: z.string().trim().email().optional().nullable().or(z.literal("").transform(() => null)),
  customerAddress: z.string().trim().max(500).optional().nullable(),
  customerVat: z.string().trim().max(30).optional().nullable(),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
  notes: z.string().trim().max(5000).optional().nullable(),
  items: z.array(docItemSchema).min(1).max(100),
});
export type InvoiceInput = z.infer<typeof invoiceInputSchema>;

export const paymentInputSchema = z.object({
  amountCents: z.coerce.number().int().min(1),
  paidAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  method: z.string().trim().max(60).optional().nullable(),
  note: z.string().trim().max(500).optional().nullable(),
});

/** Formato leggibile del numero documento, es. "PRE-2026-0012". */
export function formatDocNumber(
  docType: "quote" | "order" | "invoice",
  year: number | null,
  number: number | null,
): string {
  if (!year || !number) return "—";
  const prefix = docType === "quote" ? "PRE" : docType === "order" ? "ORD" : "FT";
  return `${prefix}-${year}-${String(number).padStart(4, "0")}`;
}
